/**
 * 时间轴布局算法工具
 * 提供等比例布局和堆叠布局两种算法
 */

export interface TimelineTask {
  id: string;
  startHour: number;
  endHour: number;
  duration: number;
  timelineHeight: number;
  // 布局属性
  layoutColumn?: number;
  layoutWidth?: number;
  layoutLeft?: number;
  stackIndex?: number; // 堆叠模式下的垂直索引
  stackHeight?: number; // 堆叠模式下的实际高度
  stackPosition?: number; // 堆叠模式下的绝对位置（从顶部开始的像素）
}

export interface LayoutResult {
  tasks: TimelineTask[];
  totalHeight: number; // 堆叠模式下的总高度
}

/**
 * 等比例布局算法（原有逻辑）
 * 重叠任务水平分割，严格按时间比例
 */
export function calculateProportionalLayout(tasks: TimelineTask[]): LayoutResult {
  if (tasks.length === 0) {
    return { tasks: [], totalHeight: 0 };
  }

  const sortedTasks = [...tasks].sort((a, b) => a.startHour - b.startHour);

  // 检测重叠任务组
  const overlapGroups: TimelineTask[][] = [];
  let currentGroup: TimelineTask[] = [];

  sortedTasks.forEach(task => {
    if (currentGroup.length === 0) {
      currentGroup = [task];
    } else {
      // 检查是否与当前组有重叠
      const hasOverlap = currentGroup.some(groupTask => 
        task.startHour < groupTask.endHour && task.endHour > groupTask.startHour
      );

      if (hasOverlap) {
        currentGroup.push(task);
      } else {
        // 没有重叠，开始新组
        overlapGroups.push([...currentGroup]);
        currentGroup = [task];
      }
    }
  });

  if (currentGroup.length > 0) {
    overlapGroups.push(currentGroup);
  }

  // 为每个重叠组分配布局
  overlapGroups.forEach(group => {
    if (group.length === 1) {
      // 单个任务，占满宽度
      group[0].layoutColumn = 0;
      group[0].layoutWidth = 1;
      group[0].layoutLeft = 0;
    } else {
      // 多个任务重叠，使用区间调度算法
      const columns: Array<{ endTime: number, tasks: TimelineTask[] }> = [];

      group.forEach(task => {
        // 找到最早结束且能容纳当前任务的列
        let bestColumn = -1;
        let earliestEndTime = Infinity;

        for (let i = 0; i < columns.length; i++) {
          if (columns[i].endTime <= task.startHour && columns[i].endTime < earliestEndTime) {
            bestColumn = i;
            earliestEndTime = columns[i].endTime;
          }
        }

        if (bestColumn === -1) {
          // 创建新列
          bestColumn = columns.length;
          columns.push({ endTime: task.endHour, tasks: [] });
        } else {
          columns[bestColumn].endTime = task.endHour;
        }

        task.layoutColumn = bestColumn;
        columns[bestColumn].tasks.push(task);
      });

      // 计算组内任务的布局
      const maxColumns = columns.length;
      group.forEach(task => {
        task.layoutWidth = 1 / maxColumns;
        task.layoutLeft = (task.layoutColumn || 0) / maxColumns;
      });
    }
  });

  // 处理没有重叠的单独任务
  sortedTasks.forEach(task => {
    if (task.layoutColumn === undefined) {
      task.layoutColumn = 0;
      task.layoutWidth = 1;
      task.layoutLeft = 0;
    }
  });

  return { 
    tasks: sortedTasks,
    totalHeight: 0 // 等比例模式不需要额外高度
  };
}

/**
 * 堆叠布局算法（新增）
 * Google Calendar 风格：任务按时间顺序垂直排列，按 cost time 比例显示高度
 */
export function calculateStackedLayout(tasks: TimelineTask[], pixelsPerHour: number = 60): LayoutResult {
  if (tasks.length === 0) {
    return { tasks: [], totalHeight: 0 };
  }

  // 按开始时间排序
  const sortedTasks = [...tasks].sort((a, b) => a.startHour - b.startHour);

  const taskSpacing = 8; // 任务间距
  let currentPosition = 0; // 当前垂直位置

  const resultTasks: TimelineTask[] = sortedTasks.map((task, index) => {
    // 计算任务高度：按 cost time 比例，最小 44px
    const taskHeight = Math.max(
      (task.duration / 60) * pixelsPerHour, // 按时长比例
      44 // 最小高度
    );

    const stackedTask = {
      ...task,
      layoutColumn: 0,
      layoutWidth: 1,
      layoutLeft: 0,
      stackIndex: index,
      stackHeight: taskHeight,
      stackPosition: currentPosition, // 在堆叠列表中的绝对位置
    };

    // 更新下一个任务的位置
    currentPosition += taskHeight + taskSpacing;

    return stackedTask;
  });

  // 总高度 = 最后一个任务的位置 + 最后一个任务的高度
  const totalHeight = currentPosition - taskSpacing; // 减去最后一个多余的间距

  console.log('堆叠布局计算结果:', {
    taskCount: resultTasks.length,
    totalHeight,
    tasks: resultTasks.map(t => ({
      id: t.id,
      startHour: t.startHour,
      duration: t.duration,
      stackHeight: t.stackHeight,
      stackPosition: t.stackPosition
    }))
  });

  return {
    tasks: resultTasks,
    totalHeight
  };
}

/**
 * 统一的布局计算入口
 */
export function calculateTimelineLayout(
  tasks: TimelineTask[],
  stackMode: boolean,
  pixelsPerHour: number = 60
): LayoutResult {
  return stackMode
    ? calculateStackedLayout(tasks, pixelsPerHour)
    : calculateProportionalLayout(tasks);
}
